import {ReactElement, useEffect, useState} from 'react'
import {Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Select, Typography} from '@material-ui/core'
import {useHistory} from 'react-router-dom'
import {Loading} from '../../Common/Loading'
import PageBody from '../../Common/PageBody'
import {useQueryApiV2} from '../../../shared/hooks/use-query-api-v2'
import {recordPromotionEvent} from './api'
import {CurrentPromotion, PromotionOffer} from './types'
import {
  highlightPromotionTitleParts,
  PROMOTION_EMPTY_BODY_COUNTRY,
  PROMOTION_EMPTY_TITLE,
  PROMOTION_HERO_READ_RULES_LABEL,
  PROMOTION_HOW_IT_WORKS,
  PROMOTION_OPEN_ACCOUNT_CTA,
  PROMOTION_READY_READ_RULES_LABEL,
  PROMOTION_RULES_SHORT,
} from './promotionCopy'
import './tenx-promotion.css'

export function PromotionPage(): ReactElement {
  const history = useHistory()
  const [promotion, {loading, error}] = useQueryApiV2<CurrentPromotion | null>({path: '/dashboard/promotions/current'})
  const [selectedMultiplier, setSelectedMultiplier] = useState<number>()
  const [selectedDeposit, setSelectedDeposit] = useState<number>()
  useEffect(() => {
    const offer = promotion?.offers[0]
    if (!offer) return
    setSelectedMultiplier((current) => current ?? offer.multiplier)
    setSelectedDeposit((current) => current ?? offer.tiers[0]?.deposit)
  }, [promotion])
  if (loading) return <Loading />
  if (error || !promotion) {
    return (
      <div className="pm-empty" data-testid="pm-unavailable">
        <Typography variant="h5" component="h2" gutterBottom>
          {PROMOTION_EMPTY_TITLE}
        </Typography>
        <Typography color="textSecondary" paragraph>
          {PROMOTION_EMPTY_BODY_COUNTRY}
        </Typography>
        <div className="pm-empty__actions">
          <Button className="pm-btn pm-btn--blue" variant="contained" disableElevation onClick={() => history.push('/accounts')}>
            Back to accounts
          </Button>
          <Button className="pm-btn--outline" variant="outlined" onClick={() => history.push('/support')}>
            Contact support
          </Button>
        </div>
      </div>
    )
  }
  const {campaign, offers, maxMultiplier} = promotion
  const {display} = campaign.rules.promotion
  const selectedOffer = offers.find((offer) => offer.multiplier === selectedMultiplier) ?? offers[0]
  const selectedTier = selectedOffer.tiers.find((tier) => tier.deposit === selectedDeposit) ?? selectedOffer.tiers[0]
  const until = `Until ${new Date(campaign.end).toLocaleDateString()}`

  const openTier = (offer: PromotionOffer, tierName: string): void => {
    recordPromotionEvent(campaign.id, {event: 'tier_open_click', multiplier: offer.multiplier, tier: tierName}).catch(() => undefined)
    history.push('/accounts/add-account', {
      accountType: 'ForexMT5SVGTauromarketsAccount',
      accountSubtype: 'mt5Std',
      currency: campaign.rules.promotion.currency,
      tauroMultiplier: offer.multiplier,
      promotionCampaignId: campaign.id,
      promotionTier: tierName,
    })
  }

  return (
    <PageBody>
      <Button color="default" onClick={() => history.push('/accounts')} style={{marginBottom: 12}}>
        ← Accounts
      </Button>

      <section className="pm-hero" data-testid="pm-hero">
        <span className="pm-hero__pill">Tauro 10x · Classic accounts · up to {maxMultiplier}x</span>
        <Typography className="pm-hero__title" component="h1">
          {highlightPromotionTitleParts(display.pageHeadline).map((part, index) =>
            part.type === 'money' ? (
              <em key={index}>{part.value}</em>
            ) : (
              <span key={index}>{part.value}</span>
            ),
          )}
        </Typography>
        <Typography className="pm-hero__text" component="p">
          {display.pageText}
        </Typography>
        <div className="pm-hero__actions">
          <Button
            className="pm-btn"
            variant="contained"
            disableElevation
            onClick={() => document.getElementById('pm-tiers')?.scrollIntoView({behavior: 'smooth'})}
          >
            Pick your tier ↓
          </Button>
          <Button
            className="pm-btn pm-btn--light"
            variant="outlined"
            disableElevation
            onClick={() => document.getElementById('pm-rules')?.scrollIntoView({behavior: 'smooth'})}
          >
            {PROMOTION_HERO_READ_RULES_LABEL}
          </Button>
        </div>
        <ul className="pm-hero__meta">
          <li>{until}</li>
          <li>You can never lose more than your deposits</li>
          <li>Leverage fixed per Factor</li>
        </ul>
      </section>

      <section className="pm-card-shell" id="pm-tiers">
        <div className="pm-card-shell__head">
          <h2>Pick your tier</h2>
          <span className="pm-until">{until}</span>
        </div>
        <p className="pm-card-shell__sub">
          All tiers are MT5 Classic accounts. The trading balance is your deposit x the Factor; the leverage is fixed per
          Factor. Pick a card to open that account.
        </p>
        {offers.map((offer) => (
          <div className="pm-group" key={offer.multiplier}>
            <div className="pm-group__head">
              <h3>
                <span className="pm-factor-badge">{offer.multiplier}x</span>
                {offer.multiplier}x factorisation
              </h3>
              <ul className="pm-group__terms">
                <li>
                  Drawdown <b>{offer.drawdownTriggerPercent}%</b>
                </li>
                <li>
                  Profit split (you / Tauro){' '}
                  <b>
                    {offer.clientProfitSplitPercent} / {offer.tauroProfitSplitPercent}
                  </b>
                </li>
                <li>
                  Leverage <b>1:{offer.leverage}</b>
                </li>
                <li>
                  Deposit{' '}
                  <b>
                    {offer.minQualifyingDeposit.toLocaleString()} to {offer.maxQualifyingDeposit.toLocaleString()} USD
                  </b>
                </li>
              </ul>
            </div>
            <Grid container spacing={2}>
              {offer.tiers.map((tier) => (
                <Grid item xs={12} sm={6} md={3} key={`${offer.multiplier}:${tier.name}`}>
                  <Card className="pm-card" elevation={0}>
                    <CardContent>
                      <Typography className="pm-card__name">
                        {tier.name} · {offer.multiplier}x
                      </Typography>
                      <Typography className="pm-card__balance">${tier.tradingBalance.toLocaleString()}</Typography>
                      <Typography>trading balance from ${tier.deposit.toLocaleString()} deposit</Typography>
                      <Typography variant="body2">1:{offer.leverage} leverage</Typography>
                      <Typography variant="body2">Closes at equity of ${tier.closesAt.toLocaleString()}</Typography>
                      <Typography variant="body2">
                        {offer.clientProfitSplitPercent} / {offer.tauroProfitSplitPercent} profit split
                      </Typography>
                      <Button
                        className="pm-card__cta"
                        disableElevation
                        disabled={!offer.canOpen}
                        onClick={() => openTier(offer, tier.name)}
                      >
                        {offer.canOpen ? 'Open this account →' : 'Closed to new accounts →'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        ))}
      </section>

      <Typography className="pm-section-title" variant="h5" component="h2">
        See what your deposit does
      </Typography>
      <div className="pm-calc">
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="promotion-factor-label">Factor</InputLabel>
              <Select
                labelId="promotion-factor-label"
                value={selectedOffer.multiplier}
                onChange={(event) => {
                  const multiplier = Number(event.target.value)
                  const offer = offers.find((item) => item.multiplier === multiplier)!
                  setSelectedMultiplier(multiplier)
                  setSelectedDeposit(offer.tiers[0]?.deposit)
                }}
              >
                {offers.map((offer) => (
                  <MenuItem key={offer.multiplier} value={offer.multiplier}>
                    {offer.multiplier}X
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="promotion-deposit-label">Deposit</InputLabel>
              <Select
                labelId="promotion-deposit-label"
                value={selectedTier.deposit}
                onChange={(event) => setSelectedDeposit(Number(event.target.value))}
              >
                {selectedOffer.tiers.map((tier) => (
                  <MenuItem key={tier.deposit} value={tier.deposit}>
                    ${tier.deposit.toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Typography>Your deposit: ${selectedTier.deposit.toLocaleString()}</Typography>
        <Typography style={{color: 'var(--pm-brand)', fontWeight: 700}}>
          Starting balance ({selectedOffer.multiplier}x): ${selectedTier.tradingBalance.toLocaleString()}
        </Typography>
        <Typography>Account closes at equity of: ${selectedTier.closesAt.toLocaleString()}</Typography>
        <Typography>The most you can lose: ${selectedTier.deposit.toLocaleString()}</Typography>
      </div>

      <section className="pm-card-shell">
        <div className="pm-card-shell__head">
          <h2>How it works</h2>
        </div>
        <ol className="pm-steps">
          {PROMOTION_HOW_IT_WORKS.map((step, index) => (
            <li key={step.title}>
              <span className="pm-step-num">{index + 1}</span>
              <b>{step.title}</b>
              <span>{step.text}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="pm-card-shell" id="pm-rules">
        <div className="pm-card-shell__head">
          <h2>The rules in short</h2>
        </div>
        <ul className="pm-rules">
          {PROMOTION_RULES_SHORT.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </section>

      <div className="pm-cta-bar">
        <div>
          <h2>Ready?</h2>
          <p>
            Pick your tier above, or start with {selectedOffer.multiplier}x. The offer runs until{' '}
            {new Date(campaign.end).toLocaleDateString()}.
          </p>
        </div>
        <div className="pm-cta-bar__actions">
          <Button
            className="pm-btn--outline"
            variant="outlined"
            onClick={() => document.getElementById('pm-rules')?.scrollIntoView({behavior: 'smooth'})}
          >
            {PROMOTION_READY_READ_RULES_LABEL}
          </Button>
          <Button
            className="pm-btn"
            variant="contained"
            disableElevation
            disabled={!selectedOffer.canOpen}
            onClick={() => openTier(selectedOffer, selectedTier.name)}
          >
            {PROMOTION_OPEN_ACCOUNT_CTA}
          </Button>
        </div>
      </div>
    </PageBody>
  )
}
